import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, RotateCcw, Square, Video, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

type VideoRecorderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  /** Tamanho máximo do arquivo (MB). A gravação para sozinha ao chegar perto do limite. */
  maxSizeMb: number;
  /** Duração máxima da gravação, em segundos. */
  maxSeconds?: number;
};

type RecorderState = "loading" | "ready" | "recording" | "recorded" | "error";

/** Taxa de vídeo usada na gravação: ~2 Mbps dá cerca de 15 MB por minuto. */
const VIDEO_BITS_PER_SECOND = 2_000_000;

/** Formatos em ordem de preferência. Chrome/Edge/Firefox gravam WEBM; Safari grava MP4. */
const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
};

const formatTime = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * Gravação de vídeo pela webcam do computador (LOT-68). No celular o FilePicker continua usando
 * a câmera nativa do aparelho. Grava com áudio quando o microfone estiver disponível.
 */
export function VideoRecorderModal({
  isOpen,
  onClose,
  onCapture,
  maxSizeMb,
  maxSeconds = 180,
}: VideoRecorderModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const sizeRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const [state, setState] = useState<RecorderState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [recorded, setRecorded] = useState<{ blob: Blob; url: string } | null>(null);
  const [hasAudio, setHasAudio] = useState(true);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const discardRecording = useCallback(() => {
    setRecorded((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  const startCamera = useCallback(async () => {
    setState("loading");
    setErrorMessage("");
    stopStream();

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Seu navegador não permite acessar a câmera.");
      }
      if (pickMimeType() === null) {
        throw new Error("Seu navegador não permite gravar vídeo. Use \"Escolher arquivo\".");
      }

      const video = { width: { ideal: 1280 }, height: { ideal: 720 } };
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video, audio: true });
        setHasAudio(true);
      } catch {
        // Sem microfone (ou permissão negada só para o áudio): grava só o vídeo.
        stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
        setHasAudio(false);
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setState("ready");
    } catch (error) {
      const err = error as DOMException;
      const message =
        err?.name === "NotAllowedError"
          ? "Permissão da câmera negada. Libere o acesso no navegador e tente novamente."
          : err?.name === "NotFoundError"
            ? "Nenhuma câmera encontrada neste computador."
            : err?.message || "Não foi possível acessar a câmera.";
      setErrorMessage(message);
      setState("error");
    }
  }, [stopStream]);

  const stopRecording = useCallback(() => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const mimeType = pickMimeType() || undefined;
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: VIDEO_BITS_PER_SECOND });
    } catch {
      recorder = new MediaRecorder(stream);
    }

    chunksRef.current = [];
    sizeRef.current = 0;
    const limitBytes = maxSizeMb * 1024 * 1024 * 0.95;

    recorder.ondataavailable = (event) => {
      if (!event.data || event.data.size === 0) return;
      chunksRef.current.push(event.data);
      sizeRef.current += event.data.size;
      if (sizeRef.current >= limitBytes) {
        toast.warning(`Gravação encerrada: o vídeo chegou perto do limite de ${maxSizeMb} MB.`);
        stopRecording();
      }
    };

    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (blob.size === 0) {
        toast.error("Não foi possível gravar o vídeo. Tente novamente.");
        setState("ready");
        return;
      }
      setRecorded({ blob, url: URL.createObjectURL(blob) });
      setState("recorded");
    };

    recorderRef.current = recorder;
    recorder.start(1000); // entrega um pedaço por segundo, para controlar o tamanho
    setElapsed(0);
    setState("recording");

    timerRef.current = window.setInterval(() => setElapsed((seconds) => seconds + 1), 1000);
  };

  // Para sozinho ao atingir a duração máxima
  useEffect(() => {
    if (state === "recording" && elapsed >= maxSeconds) {
      toast.info(`Gravação encerrada no limite de ${formatTime(maxSeconds)}.`);
      stopRecording();
    }
  }, [elapsed, maxSeconds, state, stopRecording]);

  const handleRetake = () => {
    discardRecording();
    setElapsed(0);
    setState("ready");
  };

  const handleApply = () => {
    if (!recorded) return;
    const isMp4 = recorded.blob.type.includes("mp4");
    const extension = isMp4 ? "mp4" : "webm";
    const type = isMp4 ? "video/mp4" : "video/webm";
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    onCapture(new File([recorded.blob], `video-${stamp}.${extension}`, { type }));
    handleClose();
  };

  const handleClose = () => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    stopStream();
    discardRecording();
    setElapsed(0);
    onClose();
  };

  useEffect(() => {
    if (isOpen) startCamera();
    return () => {
      clearTimer();
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex flex-col bg-black" style={{ zIndex: 99999, pointerEvents: "auto" }}>
      {/* Cabeçalho */}
      <div className="flex items-center justify-between bg-black/80 p-4">
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={handleClose}
          className="h-12 w-12 rounded-full text-white hover:bg-white/20"
          title="Fechar"
        >
          <X className="h-7 w-7" />
        </Button>
        <span className="text-lg font-semibold text-white">Gravar vídeo</span>
        <div className="h-12 w-12" />
      </div>

      {/* Visualização */}
      <div className="relative flex-1 overflow-hidden bg-black">
        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white">
            <Loader2 className="h-16 w-16 animate-spin" />
            <span className="text-lg">Iniciando câmera...</span>
          </div>
        )}

        {state === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center text-white">
            <Video className="h-20 w-20 opacity-50" />
            <p className="text-xl font-medium">Erro na câmera</p>
            <p className="text-base opacity-70">{errorMessage}</p>
            <Button type="button" onClick={startCamera} variant="secondary" className="mt-4 h-12 px-6">
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Câmera ao vivo (espelhada, como num espelho; o arquivo gravado não é espelhado) */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 h-full w-full object-contain"
          style={{
            transform: "scaleX(-1)",
            display: state === "ready" || state === "recording" ? "block" : "none",
          }}
        />

        {state === "recording" && (
          <div className="absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-white">
            <span className="h-3 w-3 animate-pulse rounded-full bg-red-500" />
            <span className="font-mono text-lg">{formatTime(elapsed)}</span>
            <span className="text-sm opacity-70">/ {formatTime(maxSeconds)}</span>
          </div>
        )}

        {state === "ready" && !hasAudio && (
          <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-amber-500/90 px-4 py-2 text-sm text-white">
            Microfone indisponível: o vídeo será gravado sem som.
          </div>
        )}

        {state === "recorded" && recorded && (
          <video
            key={recorded.url}
            src={recorded.url}
            controls
            playsInline
            className="absolute inset-0 h-full w-full bg-black object-contain"
          />
        )}
      </div>

      {/* Rodapé */}
      <div className="bg-black/80 p-6 pb-10">
        {state === "ready" && (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={startRecording}
              title="Iniciar gravação"
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-gray-400 bg-white shadow-2xl transition-transform hover:border-gray-300 active:scale-90"
            >
              <span className="h-14 w-14 rounded-full bg-red-600" />
            </button>
            <span className="text-sm text-white/70">
              Até {formatTime(maxSeconds)} ou {maxSizeMb} MB
            </span>
          </div>
        )}

        {state === "recording" && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={stopRecording}
              title="Parar gravação"
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-red-500 bg-white shadow-2xl transition-transform active:scale-90"
            >
              <Square className="h-8 w-8 fill-red-600 text-red-600" />
            </button>
          </div>
        )}

        {state === "recorded" && (
          <div className="flex justify-center gap-6">
            <Button
              type="button"
              onClick={handleRetake}
              variant="outline"
              size="lg"
              className="h-14 max-w-[160px] flex-1 rounded-full border-2 border-white bg-transparent text-white hover:bg-white/20 hover:text-white"
            >
              <RotateCcw className="mr-2 h-5 w-5" />
              Regravar
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              size="lg"
              className="h-14 max-w-[160px] flex-1 rounded-full bg-green-500 text-white hover:bg-green-600"
            >
              <Check className="mr-2 h-5 w-5" />
              Usar vídeo
            </Button>
          </div>
        )}

        {(state === "loading" || state === "error") && <div className="h-20" />}
      </div>
    </div>,
    document.body
  );
}
